import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent,
  Divider,
  Tooltip,
  CircularProgress,
  Fade,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Popover
} from '@mui/material';
import NavBar from '../components/NavBar';
import { 
  Play, 
  Pause, 
  FastForward, 
  Rewind, 
  SkipForward, 
  SkipBack,
  ExternalLink,
  MousePointer,
  Keyboard,
  Eye,
  Check,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Timer
} from 'lucide-react';
import { useSidebar } from '../contexts/SidebarContext';
import { formatDistanceToNow } from 'date-fns';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';

// Helper function for assertion severity chip color
const getSeverityColor = (severity) => {
  switch (severity?.toUpperCase()) {
    case 'MAJOR BUG':
      return 'error';
    case 'MINOR BUG':
      return 'warning';
    case 'WARNING':
      return 'info';
    default:
      return 'default';
  }
};

// Helper function for assertion result icon
const getResultIcon = (result) => {
  const props = { size: 20, style: { marginRight: '8px' } };
  switch (result?.toUpperCase()) {
    case 'PASSED':
      return <CheckCircle {...props} color="#4caf50" />;
    case 'FAILED':
      return <XCircle {...props} color="#f44336" />;
    default:
      return null;
  }
};

function TestReplay() {
  // ...existing state
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;
  const params = useParams();
  const location = useLocation();
  const runId = params.runId;
  const navigate = useNavigate();
  
  const [recording, setRecording] = useState(null);
  const [testRun, setTestRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [urlChangeLoading, setUrlChangeLoading] = useState(false);
  const [lastActionDescription, setLastActionDescription] = useState('');
  const [lastNavigationUrl, setLastNavigationUrl] = useState('');
  const [lastScreenshotIndex, setLastScreenshotIndex] = useState(-1);
  const [testStarted, setTestStarted] = useState(false);
  const [testEnded, setTestEnded] = useState(false);
  
  const timerRef = useRef(null);
  const urlChangeTimerRef = useRef(null);
  const scrollRef = useRef(null);

  // Filter events to exclude 'url_change' and 'screenshot' for the timeline display
  const visibleEvents = recording ? recording.events.filter(
    event => event.type !== 'url_change' && event.type !== 'screenshot'
  ) : [];

  // Map visible index to original index
  const getOriginalIndex = (visibleIndex) => {
    if (!recording || visibleIndex < 0 || visibleIndex >= visibleEvents.length) {
      return -1; // Or handle error appropriately
    }
    const targetVisibleEvent = visibleEvents[visibleIndex];
    return recording.events.findIndex(event => event === targetVisibleEvent);
  };

  // Map original index to visible index
  const getVisibleIndex = (originalIndex) => {
    if (!recording || originalIndex < 0 || originalIndex >= recording.events.length) {
      return -1; // Or handle error appropriately
    }
    const targetOriginalEvent = recording.events[originalIndex];
    if (targetOriginalEvent.type === 'url_change' || targetOriginalEvent.type === 'navigation') {
      // Find the *previous* visible event's index
      for (let i = originalIndex - 1; i >= 0; i--) {
        const event = recording.events[i];
        if (event.type !== 'url_change' && event.type !== 'navigation') {
          return visibleEvents.findIndex(visEvent => visEvent === event);
        }
      }
      return -1; // No visible event before this hidden one
    }
    return visibleEvents.findIndex(event => event === targetOriginalEvent);
  };

  useEffect(() => {
    const fetchTestRunData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Check if data was passed via location state
        if (location.state?.testRun) {
          // Use the test run data from the navigation state
          const runData = location.state.testRun;
          setTestRun(runData);
          
          // Fetch the recording data
          const session = await supabase.auth.getSession();
          if (!session?.data?.session?.access_token) {
            throw new Error("Not authenticated");
          }
          
          const authToken = session.data.session.access_token;
          const headers = {
            Authorization: `Bearer ${authToken}`,
          };

          const response = await axios.get(`${API_URL}/web_recording/${runData.task_id}`, { headers });
          
          if (response.data && response.data.test_run) {
            setRecording(response.data.test_run);
          } else if (response.data) {
            // Handle the direct response format with created_at, current_url, and events
            setRecording(response.data);
          } else {
            throw new Error("Invalid test run data received");
          }
        } else {
          // If no state data, fetch from API using runId
          if (!runId) {
            throw new Error("Test run ID is missing");
          }
          
          const session = await supabase.auth.getSession();
          if (!session?.data?.session?.access_token) {
            throw new Error("Not authenticated");
          }
          
          const authToken = session.data.session.access_token;
          const headers = {
            Authorization: `Bearer ${authToken}`,
          };

          const response = await axios.get(`${API_URL}/web_recording/${runId}`, { headers });
          
          if (response.data && response.data.test_run) {
            setRecording(response.data.test_run);
          } else if (response.data && Array.isArray(response.data.events)) {
            // Handle the direct response format with created_at, current_url, and events
            setRecording(response.data);
          } else {
            throw new Error("Invalid test run data received");
          }
        }
      } catch (err) {
        console.error("Error fetching test run data:", err);
        setError(err.message || "Failed to fetch test run data");
      } finally {
        setLoading(false);
      }
    };

    fetchTestRunData();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [runId, location.state]);

  // Custom function to scroll the timeline sidebar without affecting the page
  const scrollToActiveStep = () => {
    if (scrollRef.current) {
      const activeStep = scrollRef.current.querySelector('.Mui-active');
      if (activeStep) {
        // Get the container's scroll dimensions
        const container = scrollRef.current;
        const containerScrollTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        
        // Get the active step's position and dimensions
        const activeStepTop = activeStep.offsetTop;
        const activeStepHeight = activeStep.offsetHeight;
        const activeStepBottom = activeStepTop + activeStepHeight;
        
        // Define a comfortable padding (in pixels)
        const padding = 40;
        
        // Check if the element is already fully visible within the container viewport
        const isElementAboveViewport = activeStepTop < containerScrollTop + padding;
        const isElementBelowViewport = activeStepBottom > containerScrollTop + containerHeight - padding;
        
        // Only scroll if the element is not fully visible
        if (isElementAboveViewport) {
          // Element is above the visible area or too close to the top, scroll it into view with padding
          container.scrollTop = Math.max(0, activeStepTop - padding);
        } else if (isElementBelowViewport) {
          // Element is below the visible area or too close to the bottom, scroll it into view
          container.scrollTop = activeStepBottom - containerHeight + padding;
        }
        // If the element is already visible within the padding, don't scroll
      }
    }
  };
  
  useEffect(() => {
    if (isPlaying && recording && currentEventIndex < recording.events.length - 1) {
      timerRef.current = setTimeout(() => {
        setCurrentEventIndex(prev => prev + 1);
        
        // Use the custom scroll function instead
        setTimeout(scrollToActiveStep, 100);
      }, 3000 / playbackSpeed);
    } else if (isPlaying && recording && currentEventIndex >= recording.events.length - 1) {
      setIsPlaying(false);
      setTestEnded(true);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isPlaying, currentEventIndex, recording, playbackSpeed]);

  useEffect(() => {
    if (recording && currentEventIndex >= 0) {
      const currentEvent = recording.events[currentEventIndex];
      
      // Track when a new screenshot is shown
      if (currentEvent.type === 'screenshot') {
        setLastScreenshotIndex(currentEventIndex);
        // If we've been waiting for a new screenshot after URL change, clear the blur
        if (urlChangeLoading) {
          setUrlChangeLoading(false);
        }
      } else if (currentEvent.type === 'url_change' || currentEvent.type === 'navigation') {
        setUrlChangeLoading(true);
        setLastNavigationUrl(currentEvent.data.url);
        // Clear the action description when we show a navigation event
        setLastActionDescription('');
      } else if (currentEvent.type === 'action') {
        setLastActionDescription(currentEvent.data.description);
        // Clear the navigation URL when we show an action event
        setLastNavigationUrl('');
      }
      
      // Set test started if we're not at the beginning
      if (currentEventIndex > 0 && !testStarted) {
        setTestStarted(true);
      }
      
      // Set test ended if we're at the last event
      if (currentEventIndex === recording.events.length - 1) {
        setTestEnded(true);
      } else {
        setTestEnded(false);
      }
    }

    return () => {
      if (urlChangeTimerRef.current) {
        clearTimeout(urlChangeTimerRef.current);
      }
    };
  }, [currentEventIndex, recording, urlChangeLoading, testStarted]);

  const handlePlayPause = () => {
    if (!testStarted && currentEventIndex === 0) {
      setTestStarted(true);
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (recording && currentEventIndex < recording.events.length - 1) {
      if (!testStarted && currentEventIndex === 0) {
        setTestStarted(true);
      }
      setCurrentEventIndex(prev => prev + 1);
      
      // Use the custom scroll function
      setTimeout(scrollToActiveStep, 100);
    }
  };

  const handlePrevious = () => {
    if (recording && currentEventIndex > 0) {
      setCurrentEventIndex(prev => prev - 1);
      if (testEnded) {
        setTestEnded(false);
      }
      
      // Use the custom scroll function
      setTimeout(scrollToActiveStep, 100);
    }
  };

  const handleStepClick = (visibleIndex) => {
    const originalIndex = getOriginalIndex(visibleIndex);
    if (originalIndex === -1) return; // Invalid index

    setCurrentEventIndex(originalIndex);
    setIsPlaying(false);
    if (originalIndex === 0 && !testStarted) {
      setTestStarted(false);
    } else if (!testStarted) {
      setTestStarted(true);
    }
    if (originalIndex === recording.events.length - 1) {
      setTestEnded(true);
    } else {
      setTestEnded(false);
    }
    
    // Use the custom scroll function
    setTimeout(scrollToActiveStep, 100);
  };

  const handleSpeedChange = () => {
    const speeds = [1, 2, 4];
    const nextSpeedIndex = (speeds.indexOf(playbackSpeed) + 1) % speeds.length;
    setPlaybackSpeed(speeds[nextSpeedIndex]);
  };

  const handleSkipToStart = () => {
    setCurrentEventIndex(0);
    setIsPlaying(false);
    setTestStarted(false);
    setTestEnded(false);
    
    // Use the custom scroll function - scrolling to first element
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleSkipToEnd = () => {
    if (recording) {
      // Find the last *visible* event index to set as current
      const lastVisibleEventOriginalIndex = getOriginalIndex(visibleEvents.length - 1);
      // Also set the overall current index to the very last event for accurate state
      setCurrentEventIndex(recording.events.length - 1); 
      
      setIsPlaying(false);
      setTestStarted(true);
      setTestEnded(true);
      
      // Use the custom scroll function - scrolling to last element
      setTimeout(() => {
        if (scrollRef.current) {
          const lastStep = scrollRef.current.querySelector('.MuiStep-root:last-of-type');
          if (lastStep) {
            // Ensure scrolling happens *after* the state update renders the correct last step
            scrollRef.current.scrollTop = lastStep.offsetTop;
          }
        }
      }, 100);
    }
  };

  const getCurrentScreenshot = () => {
    if (!recording) return null;
    
    // Find the latest screenshot before or at the current event
    for (let i = currentEventIndex; i >= 0; i--) {
      if (recording.events[i].type === 'screenshot') {
        return recording.events[i].data.screenshot_url;
      }
    }
    return null;
  };

  // Function to determine if we should show blur
  const shouldShowBlur = () => {
    // Don't blur on test end
    if (testEnded) {
      return false;
    }
    
    if (!recording || !urlChangeLoading) {
      return false;
    }
    
    // Find if there's been a screenshot after the last URL change
    let lastUrlChangeIndex = -1;
    
    for (let i = currentEventIndex; i >= 0; i--) {
      // If we find a URL change, that's the most recent one
      if (recording.events[i].type === 'url_change') {
        lastUrlChangeIndex = i;
        break;
      }
    }
    
    // If there's been a screenshot after the last URL change, don't blur
    if (lastUrlChangeIndex > -1) {
      for (let i = currentEventIndex; i > lastUrlChangeIndex; i--) {
        if (recording.events[i].type === 'screenshot') {
          return false;
        }
      }
    }
    
    return urlChangeLoading;
  };

  const getIconForEventType = (eventType) => {
    switch (eventType) {
      case 'navigation':
      case 'url_change':
        return <ExternalLink size={16} />;
      case 'action':
        return eventType.includes('Type') ? 
          <Keyboard size={16} /> : 
          <MousePointer size={16} />;
      case 'screenshot':
        return <Eye size={16} />;
      case 'wait':
        return <Timer size={16} />;
      case 'test_ended':
        return <Check size={16} />;
      default:
        return null;
    }
  };

  const getEventDescription = (event) => {
    if (event.type === 'navigation' || event.type === 'url_change') {
      return `Navigated to: ${event.data.url}`;
    } else if (event.type === 'action') {
      return event.data.description;
    } else if (event.type === 'screenshot') {
      return 'Screenshot captured';
    } else if (event.type === 'test_ended') {
      return 'Test execution completed';
    }
    return '';
  };

  const handleReplayTest = () => {
    setCurrentEventIndex(0);
    setTestStarted(true);
    setTestEnded(false);
    setIsPlaying(true);
  };

  const handleReturnToTests = () => {
    navigate('/test');
  };

  const getLastUrlChange = () => {
    if (!recording) return '';
    
    // Find the last URL change event before or at the current event
    for (let i = currentEventIndex; i >= 0; i--) {
      const event = recording.events[i];
      if (event.type === 'url_change' || event.type === 'navigation') {
        return event.data.url;
      }
    }
    
    // If no URL change found, return the initial URL
    return recording.current_url;
  };

  return (
    <>
      <NavBar />
      
      <Box
        component="main" 
        sx={{
          minHeight: '100vh',
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          p: 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh', flexDirection: 'column' }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              Please check the test run ID or return to the test list.
            </Typography>
          </Box>
        ) : recording ? (
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                mb: 3
              }}
            >
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#fff',
                    mb: 1
                  }}
                >
                  Test Replay
                </Typography>
                <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                  Task ID: {recording.task_id} • {formatDistanceToNow(new Date(recording.created_at.$date || recording.created_at))} ago
                </Typography>
              </Box>
            </Box>

            {/* Main Content Area: Left (Timeline/Info), Right (Screenshot/Assertions) */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 3, 
              mb: 4, 
              height: 'calc(100vh - 180px)',
              minHeight: 0 // Allow container to shrink
            }}>
              
              {/* Left Column - Events Timeline */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 0, // Allow container to shrink
                height: '100%'
              }}>
                {/* Event Timeline */}
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: '#1A1A1A',
                    borderRadius: 1.5,
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    p: 2,
                    height: '100%', // Take full height
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    position: 'relative',
                    overflow: 'hidden' // Ensure content doesn't overflow
                  }}
                  ref={scrollRef}
                >
                  {/* Sticky Header Box */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: '#1A1A1A',
                    pt: 2,
                    pb: 1,
                    px: 2,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 500, 
                      }}
                    >
                      Test Events Timeline
                    </Typography>
                    
                    {/* Player Controls Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {testEnded ? (
                        // Test Ended state: Only Replay button
                        <Tooltip title="Replay Test">
                          <IconButton onClick={handleReplayTest} sx={{ color: '#fff' }} size="small">
                            <Rewind size={20} />
                          </IconButton>
                        </Tooltip>
                      ) : (!isPlaying && currentEventIndex === 0 && !testStarted) ? (
                        // Initial state: Only Play button
                        <Tooltip title="Play">
                          <IconButton onClick={handlePlayPause} sx={{ color: '#fff' }} size="small">
                            <Play size={20} />
                          </IconButton>
                        </Tooltip>
                      ) : isPlaying ? (
                        // Playing state: Pause, Rewind, FastForward, Speed
                        <>
                          <Tooltip title="Pause">
                            <IconButton onClick={handlePlayPause} sx={{ color: '#fff' }} size="small">
                              <Pause size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Previous step">
                            <IconButton onClick={handlePrevious} sx={{ color: '#fff' }} size="small">
                              <Rewind size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Next step">
                            <IconButton onClick={handleNext} sx={{ color: '#fff' }} size="small">
                              <FastForward size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Playback speed: ${playbackSpeed}x`}>
                            <IconButton onClick={handleSpeedChange} sx={{ color: '#fff' }} size="small">
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1 }}>{playbackSpeed}x</Typography>
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        // Paused state (after starting): Play, Rewind, FastForward, Speed
                        <>
                          <Tooltip title="Play">
                            <IconButton onClick={handlePlayPause} sx={{ color: '#fff' }} size="small">
                              <Play size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Previous step">
                            <IconButton onClick={handlePrevious} sx={{ color: '#fff' }} size="small">
                              <Rewind size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Next step">
                            <IconButton onClick={handleNext} sx={{ color: '#fff' }} size="small">
                              <FastForward size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={`Playback speed: ${playbackSpeed}x`}>
                            <IconButton onClick={handleSpeedChange} sx={{ color: '#fff' }} size="small">
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1 }}>{playbackSpeed}x</Typography>
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Scrollable Stepper Container */}
                  <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0 // Allow container to shrink
                  }}>
                    <Stepper 
                      activeStep={getVisibleIndex(currentEventIndex)} 
                      orientation="vertical" 
                      nonLinear
                      sx={{ 
                        pt: 2,
                        '.MuiStepConnector-line': { 
                          borderColor: 'rgba(255, 255, 255, 0.08)',
                          minHeight: '28px'
                        },
                        '.MuiStepLabel-root': {
                          padding: '8px 0'
                        },
                        '.MuiStepContent-root': {
                          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
                          marginLeft: '12px',
                          paddingLeft: '20px'
                        },
                        '.MuiStepConnector-root': {
                          marginLeft: '12px'
                        }
                      }}
                    >
                      {visibleEvents.map((event, visibleIndex) => {
                        const originalIndex = getOriginalIndex(visibleIndex);
                        const isActive = currentEventIndex === originalIndex;
                        // Determine if the step is considered "completed" in the visible timeline
                        // A step is completed if the current event index is beyond the original index of this visible step
                        const isCompleted = currentEventIndex > originalIndex;

                        // Find the corresponding original event for data access if needed,
                        // though 'event' already holds the correct event object here.
                        // const originalEvent = recording.events[originalIndex]; 

                        return (
                        <Step key={`visible-${visibleIndex}`} completed={isCompleted}>
                          <StepLabel 
                            StepIconComponent={() => (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                bgcolor: isActive ? '#1976d2' : 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                color: isActive ? '#fff' : (isCompleted ? '#4caf50' : 'rgba(255, 255, 255, 0.6)'),
                                fontSize: '0.75rem',
                                transition: 'all 0.2s ease',
                                border: isActive ? 'none' : '1px solid rgba(255, 255, 255, 0.12)'
                              }}
                              >
                                {isCompleted ? 
                                  <Check size={16} /> : 
                                  getIconForEventType(event.type)}
                              </Box>
                            )}
                            onClick={() => handleStepClick(visibleIndex)}
                          >
                            <Typography 
                              sx={{ 
                                color: isActive ? '#fff' : '#999',
                                fontSize: '0.875rem',
                                fontWeight: isActive ? 500 : 400,
                                textTransform: 'capitalize',
                                cursor: 'pointer'
                              }}
                            >
                              {event.type.replace('_', ' ')} {/* Display type */}
                            </Typography>
                          </StepLabel>
                          <StepContent>
                            <Paper 
                              elevation={0}
                              sx={{ 
                                backgroundColor: '#242424', 
                                p: 2, 
                                borderRadius: 1.5,
                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                mb: 2,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                              }}
                            >
                              {event.type === 'navigation' || event.type === 'url_change' ? (
                                <Box>
                                  <Typography sx={{ color: '#ccc', fontSize: '0.875rem', fontWeight: 500 }}>
                                    URL
                                  </Typography>
                                  <Typography sx={{ color: '#888', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                                    {event.data.url}
                                  </Typography>
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                    <ExternalLink size={16} style={{ color: '#1976d2', marginRight: '8px' }} />
                                    <Typography sx={{ color: '#ccc', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                      Navigated to page
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : event.type === 'action' ? (
                                <Box>
                                  <Typography sx={{ color: '#ccc', fontSize: '0.875rem', fontWeight: 500 }}>
                                    Action Description
                                  </Typography>
                                  <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                                    {event.data.description}
                                  </Typography>
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                    {event.data.description.toLowerCase().includes('type') ? 
                                      <Keyboard 
                                        size={16}
                                        style={{ 
                                          color: '#1976d2', 
                                          marginRight: '8px',
                                          marginTop: '2px'
                                        }} 
                                      /> : 
                                      <MousePointer 
                                        size={16}
                                        style={{ 
                                          color: '#1976d2', 
                                          marginRight: '8px',
                                          marginTop: '2px'
                                        }} 
                                      />
                                    }
                                    <Typography sx={{ color: '#ccc', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                      {event.data.description.toLowerCase().includes('type') ? 'Keyboard input' : 'Mouse action'}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : event.type === 'screenshot' ? (
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography sx={{ color: '#ccc', fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
                                    Screenshot
                                  </Typography>
                                  <Box
                                    sx={{
                                      position: 'relative',
                                      width: '100%',
                                      paddingTop: 'calc(500 / 1024 * 100%)', // 1024x500 aspect ratio
                                      maxHeight: '100px',
                                      overflow: 'hidden',
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      borderRadius: 1,
                                      margin: 'auto',
                                      backgroundColor: 'rgba(0, 0, 0, 0.2)'
                                    }}
                                  >
                                    {event.data.screenshot_url ? (
                                      <Box
                                        component="img"
                                        src={event.data.screenshot_url}
                                        alt="Screenshot Thumbnail"
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          objectPosition: 'center top'
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: 'rgba(0, 0, 0, 0.3)'
                                        }}
                                      >
                                        <Eye size={24} style={{ color: '#666', opacity: 0.7 }} />
                                      </Box>
                                    )}
                                  </Box>
                                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Eye size={16} style={{ color: '#1976d2', marginRight: '8px' }} />
                                    <Typography sx={{ color: '#ccc', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                      Visual snapshot captured
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                                  {JSON.stringify(event.data)}
                                </Typography>
                              )}
                            </Paper>
                          </StepContent>
                        </Step>
                        );
                      })}
                    </Stepper>
                  </Box>
                </Paper>
              </Box>

              {/* Right Column - Screenshot Viewer and Assertions */}
              <Box sx={{ 
                flex: 2, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 3,
                minHeight: 0, // Allow container to shrink
                height: '100%'
              }}>
                {/* Screenshot Viewer */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: '0 0 auto',
                    backgroundColor: '#1A1A1A',
                    borderRadius: 1.5,
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxHeight: '60vh'
                  }}
                >
                  <Box sx={{ 
                    p: 1.5,
                    backgroundColor: '#242424', 
                    display: 'flex', 
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}>
                    <ExternalLink size={14} style={{ color: '#777', marginRight: '6px', flexShrink: 0 }} />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        color: '#bbb',
                        fontWeight: 400,
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        width: '100%'
                      }}
                    >
                      {getLastUrlChange()?.slice(0, 100)}
                    </Typography>
                    
                    {urlChangeLoading && (
                      <LinearProgress 
                        sx={{ 
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          backgroundColor: 'transparent',
                          '.MuiLinearProgress-bar': {
                            backgroundColor: '#1976d2'
                          }
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    p: 2,
                    overflow: 'hidden'
                  }}>
                    {/* Test Start Overlay */}
                    {recording && currentEventIndex === 0 && !testStarted && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          zIndex: 10
                        }}
                      >
                        <Typography sx={{ 
                          color: '#fff', 
                          fontSize: '1.25rem', 
                          fontWeight: 500,
                          mb: 3
                        }}>
                          Start Replay
                        </Typography>
                        
                        <IconButton
                          onClick={handlePlayPause}
                          sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(25, 118, 210, 0.5)',
                            p: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.7)'
                            },
                            boxShadow: '0 0 15px rgba(25, 118, 210, 0.5)'
                          }}
                        >
                          <Play size={32} />
                        </IconButton>
                        
                        <Typography sx={{ 
                          color: '#ccc', 
                          fontSize: '0.875rem',
                          mt: 2,
                          fontStyle: 'italic'
                        }}>
                          Click to play
                        </Typography>
                      </Box>
                    )}

                    {/* Unified Subtitle for Actions & Navigation */}
                    {(lastNavigationUrl || lastActionDescription) && (
                      <Fade in={!!(lastNavigationUrl || lastActionDescription)} timeout={500}>
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            right: 16,
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            backdropFilter: 'blur(8px)',
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'flex-start',
                            opacity: 0.9,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(255, 255, 255, 0.05)'
                          }}
                        >
                          {lastNavigationUrl && (
                            <ExternalLink 
                              size={16}
                              style={{ 
                                color: '#1976d2', 
                                marginRight: '16px',
                                marginTop: '2px'
                              }} 
                            />
                          )}
                          
                          {lastActionDescription && (
                            lastActionDescription.toLowerCase().includes('type') ? 
                              <Keyboard 
                                size={16}
                                style={{ 
                                  color: '#1976d2', 
                                  marginRight: '16px',
                                  marginTop: '2px'
                                }} 
                              /> : 
                              <MousePointer 
                                size={16}
                                style={{ 
                                  color: '#1976d2', 
                                  marginRight: '16px',
                                  marginTop: '2px'
                                }} 
                              />
                          )}
                          
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#fff',
                              fontWeight: 400,
                              lineHeight: 1.5,
                              fontSize: '0.85rem',
                              letterSpacing: '0.01em'
                            }}
                          >
                            {lastNavigationUrl ? `Navigated to: ${lastNavigationUrl}` : lastActionDescription}
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                    
                    {/* Always show the aspect ratio container regardless of screenshot availability */}
                    <Fade in={true} timeout={300}>
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          paddingTop: 'calc(500 / 1024 * 100%)', // Keep the 1024:500 aspect ratio
                          overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 1,
                          margin: 'auto',
                          filter: shouldShowBlur() ? 'blur(3px)' : 'none',
                          transition: 'filter 0.3s ease',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                          backgroundColor: 'rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {getCurrentScreenshot() ? (
                          <Box
                            component="img"
                            src={getCurrentScreenshot()}
                            alt="Test Screenshot"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: 'center top',
                              zIndex: 1
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.15)',
                              zIndex: 1
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              p: 3,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: 1,
                              backdropFilter: 'blur(2px)'
                            }}>
                              <Eye size={32} style={{ color: '#888', marginBottom: '8px', opacity: 0.8 }} />
                              <Typography sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                No screenshot available for this step
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Test Ended Overlay */}
                        {recording && testEnded && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              zIndex: 2
                            }}
                          >
                            <Typography sx={{
                              color: '#fff',
                              fontSize: '1.25rem',
                              fontWeight: 500,
                              mb: 3
                            }}>
                              Test Complete
                            </Typography>

                            <IconButton
                              onClick={handleReplayTest}
                              sx={{
                                color: '#fff',
                                backgroundColor: 'rgba(25, 118, 210, 0.5)',
                                p: 2,
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.7)'
                                },
                                boxShadow: '0 0 15px rgba(25, 118, 210, 0.5)'
                              }}
                            >
                              <Rewind size={32} />
                            </IconButton>

                            <Typography sx={{
                              color: '#ccc',
                              fontSize: '0.875rem',
                              mt: 2,
                              fontStyle: 'italic'
                            }}>
                              Replay recording
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Fade>
                  </Box>
                  
                </Paper>

                {/* Assertions List */}
                {testRun?.result_dict?.assertions && testRun.result_dict.assertions.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: 'transparent',
                      borderLeft: '1px solid rgba(255, 255, 255, 0.05)',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0, // Allow container to shrink
                      overflow: 'hidden' // Ensure content doesn't overflow
                    }}
                  >
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        px: 2,
                        py: 1,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}
                    >
                      Assertions
                    </Typography>
                    <Box sx={{
                      flex: 1,
                      overflowY: 'auto',
                      minHeight: 0 // Allow container to shrink
                    }}>
                      <List dense sx={{ py: 0.5 }}>
                        {testRun.result_dict.assertions.map((assertion, index) => (
                          <ListItem 
                            key={index} 
                            sx={{ 
                              py: 0.5,
                              px: 2
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                              <ListItemIcon sx={{ minWidth: 'auto' }}>
                                {getResultIcon(assertion.result)}
                              </ListItemIcon>
                              <ListItemText 
                                primary={assertion.statement} 
                                primaryTypographyProps={{ 
                                  sx: { color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 } 
                                }}
                                secondary={assertion.result?.toUpperCase() === 'FAILED' && assertion.failure_reason ? assertion.failure_reason : null}
                                secondaryTypographyProps={{
                                  sx: { color: 'rgba(255, 114, 114, 0.8)', fontSize: '0.7rem', mt: 0.25, fontStyle: 'italic' }
                                }}
                                sx={{ m: 0 }} 
                              />
                              {assertion.severity && (
                                <Chip 
                                  label={assertion.severity} 
                                  size="small" 
                                  color={getSeverityColor(assertion.severity)} 
                                  sx={{ 
                                    height: '16px', 
                                    fontSize: '0.6rem', 
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    opacity: 0.7,
                                    '& .MuiChip-label': {
                                      px: 0.75
                                    }
                                  }} 
                                />
                              )}
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
            
            {/* Current Action Information - Check original event type */}
            {recording.events[currentEventIndex]?.type === 'action' && (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#1A1A1A',
                  borderRadius: 1.5,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  p: 3,
                  mb: 4,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 500, 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {recording.events[currentEventIndex]?.type === 'action' && (
                    recording.events[currentEventIndex].data.description.toLowerCase().includes('type') ? 
                      <Keyboard style={{ marginRight: '16px', color: '#1976d2', fontSize: '1.1rem' }} /> : 
                      <MousePointer style={{ marginRight: '16px', color: '#1976d2', fontSize: '1.1rem' }} />
                  )}
                  Current Action
                </Typography>
                <Typography sx={{ color: '#ccc', fontSize: '1rem', lineHeight: 1.6 }}>
                  {recording.events[currentEventIndex]?.data.description}
                </Typography>
              </Paper>
            )}
            
            {/* URL change indicator - Check original event type */}
            {recording.events[currentEventIndex]?.type === 'url_change' && (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#1A1A1A',
                  borderRadius: 1.5,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  p: 3,
                  mb: 4,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 500, 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ExternalLink style={{ marginRight: '16px', color: '#1976d2', fontSize: '1.1rem' }} />
                  Navigation
                </Typography>
                <Typography sx={{ color: '#ccc', fontSize: '1rem', lineHeight: 1.6 }}>
                  Navigated to: {recording.events[currentEventIndex]?.data.url}
                </Typography>
              </Paper>
            )}
            
            {/* Test Ended Information */}
            {recording && testEnded && (
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: '#1A1A1A',
                  borderRadius: 1.5,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  p: 3,
                  mb: 4,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 500, 
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Check style={{ marginRight: '16px', color: '#4caf50', fontSize: '1.1rem' }} />
                  Test Ended
                </Typography>
                <Typography sx={{ color: '#ccc', fontSize: '1rem', lineHeight: 1.6 }}>
                  Test execution has been completed.
                </Typography>
              </Paper>
            )}
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <Typography variant="body1" sx={{ color: '#888' }}>
              No recording data available.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}

export default TestReplay; 